import { nanoid } from 'nanoid';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { ChatMessage } from './types';
import { useRef, useState } from 'react';

// Module-level cache so the conversation survives the chat component being
// unmounted (the chat panel is conditionally rendered — closing it destroys
// component state). Keyed per experience.
//
// The page-session lifetime is deliberate: it must survive chat close/reopen
// AND SPA route changes back to the same experience (mirroring the backend,
// which keeps the conversation alive via the session_id in sessionStorage).
// A full page load starts fresh. Memory is bounded by experiences visited
// per page session, each holding one chat transcript.
const chatHistoryCache = new Map<string, ChatMessage[]>();

export const useChatbot = <T>(
  url: string,
  {
    onOpen,
    onClose,
    onError,
    reconnectAttempts,
    reconnectInterval,
    shouldReconnect,
    onMessage,
    firstQuery,
    historyKey,
  }: {
    reconnectAttempts?: number;
    reconnectInterval?: number;
    onOpen?: (openEvent: WebSocketEventMap['open']) => void;
    shouldReconnect?: (closeEvent: WebSocketEventMap['close']) => boolean;
    onClose?: (closeEvent: WebSocketEventMap['close']) => void;
    onError?: (errorEvent: WebSocketEventMap['error']) => void;
    onMessage?: (message: T) => void;
    firstQuery?: string;
    /** Restores/persists the message history across remounts under this key. */
    historyKey?: string;
  },
) => {
  const [connect, setConnect] = useState(false);
  const authMessageReceived = useRef(false);
  const socketUrl = url;
  const { sendJsonMessage, readyState } = useWebSocket(
    socketUrl,
    {
      reconnectInterval,
      reconnectAttempts,
      onMessage: (event) => {
        if (!authMessageReceived.current) {
          authMessageReceived.current = true;
          const data = JSON.parse(event.data) as Record<string, unknown>;
          onMessage?.(data as T);
          // When history was restored from the cache (chat reopened in the
          // same page session), the welcome message is already in it — don't
          // append a duplicate or replay the first query.
          const hasRestoredHistory = messageHistoryRef.current.length > 0;
          if (!hasRestoredHistory) {
            if (firstQuery) {
              // every message have a metadata object including the welcome message
              // but if we have a first query we don't want to display the metadata associated with the it
              // just the metadata associated with the first query
              addMessage({ ...data, metadata: null } as ChatMessage);
              sendQuery(firstQuery);
            } else {
              addMessage(data as ChatMessage);
            }
          }
          setIsReady(true);
          return;
        }
        handleMessage(event);
      },
      onOpen,
      shouldReconnect,
      onClose,
      onError,
    },
    connect,
  );
  const initialHistory = historyKey
    ? (chatHistoryCache.get(historyKey) ?? [])
    : [];
  const [messageHistory, setMessageHistory] =
    useState<ChatMessage[]>(initialHistory);
  const messageHistoryRef = useRef<ChatMessage[]>(initialHistory);
  const [isBusy, setIsBusy] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const persistHistory = (messages: ChatMessage[]) => {
    if (historyKey) chatHistoryCache.set(historyKey, messages);
  };

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];

  const addMessage = (message: ChatMessage) => {
    messageHistoryRef.current = [...messageHistoryRef.current, message];
    setMessageHistory(messageHistoryRef.current);
    persistHistory(messageHistoryRef.current);
  };

  const sendQuery = (query: string) => {
    const queryId = nanoid();
    const data = {
      action: 'Query',
      query: query,
      query_id: queryId,
      message: '',
      stream: true,
    };
    addMessage({ ...data, queryId, is_final: false });
    sendJsonMessage(data);
    setIsBusy(true);
    return queryId;
  };

  const handleMessage = (lastMessage: MessageEvent<string> | null) => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data) as Partial<ChatMessage>
      const existingMessageIndex = messageHistoryRef.current.findIndex(
        (message) => {
          return !!(message.query_id && message.query_id === data.query_id);
        },
      );
      // The indexed access needs a guard even though findIndex >= 0
      // guarantees presence — strict consumer tsconfigs treat indexed
      // reads as possibly undefined.
      const current =
        existingMessageIndex >= 0
          ? messageHistoryRef.current[existingMessageIndex]
          : undefined;
      if (current) {
        const messageChunk = current.message + (data.chunk ?? '');
        const updatedMessage: ChatMessage = {
          ...current,
          ...data,
          message: messageChunk,
          is_final: data.is_final,
        };
        const newMessagesList = [...messageHistoryRef.current];
        newMessagesList[existingMessageIndex] = updatedMessage;
        messageHistoryRef.current = newMessagesList;
      } else {
        const newMessagesList = [
          ...messageHistoryRef.current,
          data as ChatMessage,
        ];
        messageHistoryRef.current = newMessagesList;
      }
      setMessageHistory(messageHistoryRef.current);
      persistHistory(messageHistoryRef.current);
      setIsBusy(false);
    }
  };

  const requestTranscript = (email: string) => {
    const data = { action: 'RequestTranscript', email };
    sendJsonMessage(data);
  };

  const isTranscriptRequestAcknowledged =
    messageHistory.find((x) => x.query_id === 'transcript_registered')
      ?.is_final === true;

  return {
    isReady,
    sendQuery,
    messageHistory: messageHistory.filter(
      (x) => x.action !== 'RequestTranscript',
    ),
    addMessage,
    connectionStatus,
    isConnected: connectionStatus === 'Open',
    isBusy,
    suggestions: [],
    requestTranscript,
    isTranscriptRequestAcknowledged,
    sendJsonMessage,
    connect: () => setConnect(true),
    /**
     * Drop the current socket and open a new one — used to retry a handshake
     * that errored or was never answered. The session id lives in the caller,
     * so the backend resumes the same conversation on the new socket.
     */
    reconnect: () => {
      // The next frame on the new socket is the welcome message again.
      authMessageReceived.current = false;
      setIsReady(false);
      // react-use-websocket only establishes a socket on a false -> true edge,
      // so the teardown has to commit before we ask for a new one. Deferring by
      // a task lets React flush this render first.
      setConnect(false);
      globalThis.setTimeout(() => setConnect(true), 0);
    },
    disconnect: () => {
      sendJsonMessage({ action: 'Disconnect' });
    },
  };
};
